<script setup lang="ts">
import { router } from '@inertiajs/vue3';
import { Globe } from 'lucide-vue-next';
import { computed } from 'vue';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { useTranslations } from '@/composables/useTranslations';
import { setUrlDefaults } from '@/wayfinder';

withDefaults(
    defineProps<{
        /** Header chip vs full-width in mobile drawer */
        variant?: 'inline' | 'drawer';
        align?: 'start' | 'center' | 'end';
    }>(),
    {
        variant: 'inline',
        align: 'end',
    },
);

const { locale, t } = useTranslations();

const currentLocale = computed(() => (locale.value === 'sr' ? 'sr' : 'en'));

function pathForLocale(newLocale: 'en' | 'sr'): string {
    const path = window.location.pathname;
    const segments = path.split('/').filter(Boolean);

    if (segments.length > 0 && (segments[0] === 'en' || segments[0] === 'sr')) {
        segments[0] = newLocale;
    } else {
        segments.unshift(newLocale);
    }

    return (
        `/${segments.join('/')}` + window.location.search + window.location.hash
    );
}

function onLocaleChange(value: unknown) {
    if (value !== 'en' && value !== 'sr') {
        return;
    }

    if (value === currentLocale.value) {
        return;
    }

    setUrlDefaults({ locale: value });

    router.visit(pathForLocale(value), {
        preserveScroll: true,
        preserveState: true,
    });
}
</script>

<template>
    <div
        :class="
            variant === 'drawer'
                ? 'border-t border-sidebar-border/80 px-1 pt-5'
                : 'flex items-center'
        "
    >
        <span
            v-if="variant === 'drawer'"
            class="mb-2 block text-xs font-medium text-muted-foreground"
        >
            {{ t('tools.language.label', 'Language') }}
        </span>
        <Select
            :model-value="currentLocale"
            @update:model-value="onLocaleChange"
        >
            <SelectTrigger
                size="sm"
                :aria-label="t('tools.language.label', 'Language')"
                :class="[
                    'gap-2 border-border bg-background/90 shadow-sm backdrop-blur-sm transition-colors dark:bg-background/50',
                    variant === 'drawer'
                        ? 'h-11 w-full justify-between sm:h-10'
                        : 'h-11 max-w-full min-w-0 justify-between sm:h-9 sm:min-w-[10rem]',
                ]"
            >
                <span class="flex min-w-0 flex-1 items-center gap-2 text-left">
                    <Globe class="size-3.5 shrink-0 text-muted-foreground" />
                    <SelectValue
                        :placeholder="
                            t(
                                'tools.language.select_placeholder',
                                'Select language',
                            )
                        "
                    />
                </span>
            </SelectTrigger>
            <SelectContent
                :align="align"
                :side-offset="4"
                class="min-w-[var(--reka-select-trigger-width)]"
            >
                <SelectItem value="en" class="cursor-pointer py-2">
                    <span class="font-medium">{{
                        t('tools.language.option_en', 'English')
                    }}</span>
                </SelectItem>
                <SelectItem value="sr" class="cursor-pointer py-2">
                    <span class="font-medium">{{
                        t('tools.language.option_sr', 'Serbian')
                    }}</span>
                </SelectItem>
            </SelectContent>
        </Select>
    </div>
</template>
